(() => {
  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const isVisible = (element) => {
    if (!element) return false;
    const style = window.getComputedStyle(element);
    const rect = element.getBoundingClientRect();
    return style.display !== 'none' && style.visibility !== 'hidden' && Number(style.opacity || 1) !== 0 && rect.width > 0 && rect.height > 0;
  };

  const initWelcomePopup = () => {
    const popup = $('[data-wm-welcome-popup]');
    if (!popup) return;

    const panel = $('.wm-welcome__panel', popup);
    const success = $('[data-wm-welcome-success]', popup);
    const seenKey = 'wm-welcome-popup-seen-v1';
    const subscribedKey = 'wm-welcome-popup-subscribed-v1';
    const discountAppliedKey = 'wm-take20-applied-v1';
    const returnKey = 'wm-welcome-return-to-v2';
    const params = new URLSearchParams(window.location.search);
    const forcePreview = params.get('show_welcome_popup') === '1';
    const discountCode = 'TAKE20';
    let opened = false;
    let retryTimer;

    const rememberReturnPage = () => {
      if (!window.sessionStorage.getItem(returnKey)) {
        const current = `${window.location.pathname}${window.location.search}${window.location.hash}`;
        window.sessionStorage.setItem(returnKey, current);
      }
    };

    const privacyBannerVisible = () => {
      const selectors = [
        '#shopify-pc__banner',
        '.shopify-pc__banner__dialog',
        '[id^="shopify-pc__"]',
        '[data-shopify-privacy-banner]'
      ];
      return selectors.some((selector) => $$(selector).some(isVisible));
    };

    const openPopup = () => {
      if (opened || !popup.hidden) return;
      if (privacyBannerVisible()) {
        window.clearTimeout(retryTimer);
        retryTimer = window.setTimeout(openPopup, 1500);
        return;
      }
      rememberReturnPage();
      opened = true;
      popup.hidden = false;
      document.body.classList.add('wm-welcome-open');
      window.requestAnimationFrame(() => panel?.focus());
    };

    const closePopup = () => {
      popup.hidden = true;
      document.body.classList.remove('wm-welcome-open');
      window.localStorage.setItem(success ? subscribedKey : seenKey, 'true');
    };

    popup.querySelectorAll('[data-wm-welcome-close]').forEach((button) => button.addEventListener('click', closePopup));
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !popup.hidden) closePopup();
    });

    const copyButton = $('[data-wm-copy-code]', popup);
    copyButton?.addEventListener('click', async () => {
      const code = $('[data-wm-welcome-code]', popup)?.textContent.trim();
      if (!code) return;
      try {
        await navigator.clipboard.writeText(code);
        copyButton.textContent = 'Copied';
      } catch (error) {
        copyButton.textContent = code;
      }
    });

    const hasSeen = window.localStorage.getItem(seenKey) || window.localStorage.getItem(subscribedKey);
    const isCart = /^\/cart(?:\/|$)/.test(window.location.pathname);

    if (success) {
      window.localStorage.setItem(subscribedKey, 'true');
      if (!window.sessionStorage.getItem(discountAppliedKey)) {
        const returnTo = window.sessionStorage.getItem(returnKey) || window.location.pathname || '/';
        window.sessionStorage.setItem(discountAppliedKey, 'true');
        window.location.replace(`/discount/${discountCode}?redirect=${encodeURIComponent(returnTo)}`);
        return;
      }
      openPopup();
      return;
    }

    if (forcePreview) {
      openPopup();
      return;
    }

    if (hasSeen || isCart || window.Shopify?.designMode) return;

    // Show the offer 10 seconds after the page loads, for every visitor.
    window.setTimeout(openPopup, 10000);
  };

  const initCollectionLoadMore = () => {
    const grid = $('[data-collection-grid]');
    const link = $('[data-collection-load-more]');
    const wrap = $('[data-collection-load-more-wrap]');
    const status = $('[data-collection-load-more-status]');
    if (!grid || !link || !window.fetch) return;

    const preservePreviewContext = (href) => {
      const nextUrl = new URL(href, window.location.href);
      const currentUrl = new URL(window.location.href);
      ['preview_theme_id', '_fd', 'pb'].forEach((key) => {
        if (currentUrl.searchParams.has(key)) nextUrl.searchParams.set(key, currentUrl.searchParams.get(key));
      });
      return nextUrl.toString();
    };

    link.href = preservePreviewContext(link.getAttribute('href') || link.href);
    let loading = false;
    link.addEventListener('click', async (event) => {
      event.preventDefault();
      if (loading) return;
      loading = true;
      const originalText = link.textContent;
      link.setAttribute('aria-busy', 'true');
      link.textContent = 'Loading artwork…';
      if (status) status.textContent = '';

      try {
        const response = await fetch(link.href, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
        if (!response.ok) throw new Error('Unable to load next collection page');
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const nextGrid = doc.querySelector('[data-collection-grid]');
        if (!nextGrid) throw new Error('Collection grid not found');

        const items = Array.from(nextGrid.children);
        const fragment = document.createDocumentFragment();
        items.forEach((item) => fragment.appendChild(item));
        grid.appendChild(fragment);

        const nextLink = doc.querySelector('[data-collection-load-more]');
        if (nextLink?.getAttribute('href')) {
          link.href = preservePreviewContext(nextLink.getAttribute('href'));
          link.textContent = originalText;
          link.removeAttribute('aria-busy');
        } else if (wrap) {
          wrap.hidden = true;
        }

        if (status) status.textContent = `${items.length} more artworks loaded`;
      } catch (error) {
        window.location.assign(link.href);
        return;
      } finally {
        loading = false;
        link.removeAttribute('aria-busy');
        if (!wrap?.hidden) link.textContent = originalText;
      }
    });
  };

  const initMenuPolish = () => {
    const toggle = $('[data-menu-toggle][aria-controls="WallMatterMenu"]');
    const menu = $('#WallMatterMenu');
    if (!toggle || !menu) return;

    const close = () => {
      menu.hidden = true;
      toggle.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('menu-open');
    };

    document.addEventListener('click', (event) => {
      if (menu.hidden || menu.contains(event.target) || toggle.contains(event.target)) return;
      close();
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !menu.hidden) {
        close();
        toggle.focus();
      }
    });
    menu.addEventListener('click', (event) => {
      if (event.target.closest('a')) close();
    });
  };

  const initProductConversion = () => {
    const product = $('.spoils-product');
    if (!product) return;
    const form = $('.spoils-product__form', product);
    const submit = form?.querySelector('button[type="submit"]');
    if (!form || !submit) return;

    const selectedPrice = () => {
      const configPrice = $('[data-wm-config-price]', product)?.textContent.trim();
      if (configPrice) return configPrice;
      const select = form.querySelector('select[name="id"]');
      const selectedText = select?.selectedOptions?.[0]?.textContent || '';
      const pieces = selectedText.split('—').map((piece) => piece.trim()).filter(Boolean);
      if (pieces.length > 1) return pieces[pieces.length - 1].replace(/—\s*Sold out$/i, '').trim();
      return $('.spoils-product__price', product)?.textContent.replace(/^Starting at\s*/i, '').trim() || '';
    };

    const selectedLabel = () => {
      const size = form.querySelector('input[name="wm-size"]:checked')?.value;
      const frame = form.querySelector('input[name="wm-frame"]:checked')?.value;
      if (size || frame) return [size, frame].filter(Boolean).join(' · ');
      const select = form.querySelector('select[name="id"]');
      const text = select?.selectedOptions?.[0]?.textContent || '';
      return text.split('—')[0]?.trim() || 'Selected artwork';
    };

    const bar = document.createElement('div');
    bar.className = 'wm-mobile-atc';
    bar.hidden = true;
    bar.innerHTML = '<div class="wm-mobile-atc__meta"><span data-mobile-atc-selection>Selected artwork</span><strong data-mobile-atc-price></strong></div><div class="wm-mobile-atc__note">Spend $500 get FREE SHIPPING</div><button class="button button--dark" type="button" data-mobile-atc-submit>Add to cart</button>';
    document.body.appendChild(bar);

    const mobileSubmit = $('[data-mobile-atc-submit]', bar);
    const mobilePrice = $('[data-mobile-atc-price]', bar);
    const mobileSelection = $('[data-mobile-atc-selection]', bar);

    const sync = () => {
      const unavailable = submit.disabled || /sold out|unavailable/i.test(submit.textContent);
      if (!unavailable) submit.textContent = 'Add to cart';
      if (mobileSubmit) {
        mobileSubmit.disabled = unavailable;
        mobileSubmit.textContent = unavailable ? 'Sold out' : 'Add to cart';
      }
      if (mobilePrice) mobilePrice.textContent = selectedPrice();
      if (mobileSelection) mobileSelection.textContent = selectedLabel();
    };

    form.addEventListener('change', () => window.setTimeout(sync, 0));
    sync();

    mobileSubmit?.addEventListener('click', () => {
      if (mobileSubmit.disabled) return;
      if (typeof form.requestSubmit === 'function') form.requestSubmit(submit);
      else submit.click();
    });

    const details = product.querySelector('[id^="ProductDetails-"]') || $('.spoils-product__overview', product);
    const showBar = () => {
      bar.hidden = false;
      document.body.classList.add('wm-mobile-atc-active');
      sync();
    };

    if (details && 'IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          showBar();
          observer.disconnect();
        }
      }, { threshold: 0.08 });
      observer.observe(details);
    } else {
      showBar();
    }
  };

  document.addEventListener('DOMContentLoaded', () => {
    initWelcomePopup();
    initCollectionLoadMore();
    initMenuPolish();
    initProductConversion();
  });
})();
